import type { PaginatedExtendedGroupByQuery } from '@/api/generated/model';
import {
  AIFI_LEVELS,
  DIMENSION_SCORE_CEILING,
  PROMOTION_NEXT_LEVEL,
  PROMOTION_SOURCE_LEVELS,
  type AifiLevel,
} from '@/domains/pages/ai-proficiency/constants';
import type { LevelDimensionTargets } from '@/domains/pages/ai-proficiency/fixtures/upskilling-benchmarks';
import type { MethodologyDimension } from '@/domains/pages/ai-proficiency/fixtures/methodology';
import { mapMethodologyDimensionsFromAggregates } from '@/domains/pages/ai-proficiency/fixtures/methodology';
import type { LevelSnapshot } from '@/domains/pages/ai-proficiency/utils/map-aggregates';

const LEVEL_SET = new Set<string>(AIFI_LEVELS);

export type RankedDimension = MethodologyDimension & {
  impact: number;
  rank: number;
};

export type PromotionTransition = {
  fromLevel: (typeof PROMOTION_SOURCE_LEVELS)[number];
  toLevel: AifiLevel;
  count: number;
  levelTotal: number;
  percentOfLevel: number;
};

export function computeDimensionImpact(score: number, weight: number, ceiling = DIMENSION_SCORE_CEILING): number {
  return Math.max(0, ceiling - score) * weight;
}

export function rankDimensionsByImpact(
  dimensions: MethodologyDimension[],
  ceiling = DIMENSION_SCORE_CEILING,
): RankedDimension[] {
  return [...dimensions]
    .map((dimension) => ({
      ...dimension,
      impact: computeDimensionImpact(dimension.score, dimension.weight, ceiling),
      rank: 0,
    }))
    .sort((a, b) => b.impact - a.impact || b.weight - a.weight)
    .map((dimension, index) => ({ ...dimension, rank: index + 1 }));
}

/** Rank dimensions by raw score ascending (lowest scores first, including zeros). */
export function rankDimensionsByLowestScore(dimensions: MethodologyDimension[]): RankedDimension[] {
  return [...dimensions]
    .map((dimension) => ({
      ...dimension,
      impact: dimension.score,
      rank: 0,
    }))
    .sort((a, b) => a.score - b.score || b.weight - a.weight)
    .map((dimension, index) => ({ ...dimension, rank: index + 1 }));
}

export function getWorstTwoDimensionKeys(ranked: RankedDimension[]): { worst: string; secondWorst: string } {
  return {
    worst: ranked[0]?.key ?? '',
    secondWorst: ranked[1]?.key ?? '',
  };
}

/** Sort key for drawer ordering: closest to per-dimension target first. */
export function dimensionTargetSortKey(score: number, target: number, level: AifiLevel): number {
  if (score < target) return target - score;
  if (level === 'Expert' && score > target) return 1000 + (100 - score);
  return 2000 + Math.abs(target - score);
}

export function rankDimensionsByTarget(
  dimensions: MethodologyDimension[],
  level: AifiLevel,
  levelTargets: LevelDimensionTargets,
): RankedDimension[] {
  return [...dimensions]
    .map((dimension) => {
      const target = levelTargets[dimension.key] ?? DIMENSION_SCORE_CEILING;
      const sortKey = dimensionTargetSortKey(dimension.score, target, level);
      return {
        ...dimension,
        impact: sortKey,
        rank: 0,
      };
    })
    .sort((a, b) => a.impact - b.impact || b.weight - a.weight)
    .map((dimension, index) => ({ ...dimension, rank: index + 1 }));
}

export function mapLevelMethodologyFromAggregates(
  data: PaginatedExtendedGroupByQuery | undefined,
): Partial<Record<AifiLevel, MethodologyDimension[]>> {
  if (!data) return {};

  const result: Partial<Record<AifiLevel, MethodologyDimension[]>> = {};

  for (const row of data.data) {
    const level = row.group_name;
    if (!LEVEL_SET.has(level)) continue;

    const dimensions = mapMethodologyDimensionsFromAggregates(row);
    if (dimensions) {
      result[level as AifiLevel] = dimensions;
    }
  }

  return result;
}

export function mapPromotionTransitions(
  data: PaginatedExtendedGroupByQuery | undefined,
  levels: LevelSnapshot[],
): PromotionTransition[] {
  const countByLevel = new Map<AifiLevel, number>();

  if (data) {
    for (const row of data.data) {
      const level = row.group_name;
      if (!LEVEL_SET.has(level)) continue;
      countByLevel.set(level as AifiLevel, row.n_users ?? 0);
    }
  }

  const levelMap = new Map(levels.map((level) => [level.level, level]));

  return PROMOTION_SOURCE_LEVELS.map((fromLevel) => {
    const levelTotal = levelMap.get(fromLevel)?.userCount ?? 0;
    const count = countByLevel.get(fromLevel) ?? 0;
    const toLevel = PROMOTION_NEXT_LEVEL[fromLevel];

    return {
      fromLevel,
      toLevel,
      count,
      levelTotal,
      percentOfLevel: levelTotal > 0 ? (count / levelTotal) * 100 : 0,
    };
  });
}

export function sumPromotionCounts(transitions: PromotionTransition[]): number {
  return transitions.reduce((sum, transition) => sum + transition.count, 0);
}

export type UpskillingLoadingStateInput = {
  view: 'all' | 'team' | 'market' | 'tenure';
  segmentListFetched: boolean;
  segmentsCount: number;
  selectedSegmentUserCount: number | null;
  scopeReady: boolean;
  scopeQueriesLoading: boolean;
};

/** True while charts refetch after a scope or segment change (picker stays mounted). */
export function resolveUpskillingChartsLoading({
  view,
  segmentListFetched,
  segmentsCount,
  selectedSegmentUserCount,
  scopeReady,
  scopeQueriesLoading,
}: UpskillingLoadingStateInput): boolean {
  if (view !== 'all' && !segmentListFetched) return true;

  if (view !== 'all') {
    if (segmentsCount === 0) return false;
    if (selectedSegmentUserCount === 0) return false;
  }

  if (!scopeReady) return false;
  return scopeQueriesLoading;
}
